import { useState, useCallback } from 'react';

export type ValidationFunction<T> = (value: T) => string | null;

export interface FormState<T extends Record<string, any>> {
  values: T;
  errors: Partial<Record<keyof T, string>>;
  touched: Partial<Record<keyof T, boolean>>;
  isValid: boolean;
}

export interface UseFormReturn<T extends Record<string, any>> {
  values: T;
  errors: Partial<Record<keyof T, string>>;
  touched: Partial<Record<keyof T, boolean>>;
  isValid: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  setFieldValue: (name: keyof T, value: any) => void;
  setFieldTouched: (name: keyof T, isTouched: boolean) => void;
  setFieldError: (name: keyof T, error: string | null) => void;
  validateField: (name: keyof T) => boolean;
  validateForm: () => boolean;
  resetForm: () => void;
}

export const useForm = <T extends Record<string, any>>(
  initialValues: T,
  validationSchema?: Record<keyof T, ValidationFunction<any>>
): UseFormReturn<T> => {
  const [formState, setFormState] = useState<FormState<T>>({
    values: initialValues,
    errors: {},
    touched: {},
    isValid: true,
  });

  // Validate a single field
  const validateField = useCallback(
    (name: keyof T): boolean => {
      if (!validationSchema || !validationSchema[name]) {
        return true;
      }

      const error = validationSchema[name](formState.values[name]);
      
      setFormState((prevState) => ({
        ...prevState,
        errors: {
          ...prevState.errors,
          [name]: error,
        },
      }));

      return !error;
    },
    [formState.values, validationSchema]
  );

  // Validate the entire form
  const validateForm = useCallback((): boolean => {
    if (!validationSchema) {
      return true;
    }

    const newErrors: Partial<Record<keyof T, string>> = {};
    let isValid = true;

    Object.keys(validationSchema).forEach((key) => {
      const fieldKey = key as keyof T;
      const error = validationSchema[fieldKey](formState.values[fieldKey]);
      
      if (error) {
        newErrors[fieldKey] = error;
        isValid = false;
      }
    });

    setFormState((prevState) => ({
      ...prevState,
      errors: newErrors,
      isValid,
    }));

    return isValid;
  }, [formState.values, validationSchema]);

  // Handle input change
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, type, checked } = e.target;
      const fieldValue = type === 'checkbox' ? checked : value;
      
      setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          [name]: fieldValue,
        },
      }));

      if (validationSchema && validationSchema[name as keyof T]) {
        validateField(name as keyof T);
      }
    },
    [validateField, validationSchema]
  );

  // Handle input blur
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const { name } = e.target;
      
      setFormState((prevState) => ({
        ...prevState,
        touched: {
          ...prevState.touched,
          [name]: true,
        },
      }));

      if (validationSchema && validationSchema[name as keyof T]) {
        validateField(name as keyof T);
      }
    },
    [validateField, validationSchema]
  );

  // Set field value programmatically
  const setFieldValue = useCallback(
    (name: keyof T, value: any) => {
      setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          [name]: value,
        },
      }));

      if (validationSchema && validationSchema[name]) {
        validateField(name);
      }
    },
    [validateField, validationSchema]
  );

  // Set field touched state programmatically
  const setFieldTouched = useCallback((name: keyof T, isTouched: boolean) => {
    setFormState((prevState) => ({
      ...prevState,
      touched: {
        ...prevState.touched,
        [name]: isTouched,
      },
    }));
  }, []);

  // Set field error programmatically
  const setFieldError = useCallback((name: keyof T, error: string | null) => {
    setFormState((prevState) => ({
      ...prevState,
      errors: {
        ...prevState.errors,
        [name]: error,
      },
    }));
  }, []);

  // Reset form to initial values
  const resetForm = useCallback(() => {
    setFormState({
      values: initialValues,
      errors: {},
      touched: {},
      isValid: true,
    });
  }, [initialValues]);

  return {
    values: formState.values,
    errors: formState.errors,
    touched: formState.touched,
    isValid: formState.isValid,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    validateField,
    validateForm,
    resetForm,
  };
};